import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  Grid,
  MenuItem, // Standard MenuItem
  Modal,
  OutlinedInput,
  Select, // Standard Select
  Typography,
  Chip, // Added
  Checkbox, // Added
  FormControl,
  IconButton,
  AccordionDetails,
  AccordionProps,
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionSummaryProps,
  accordionSummaryClasses,
} from "@mui/material";
import { FontGrayNormal, ModalTitle } from "../modals/style"; // Assuming these exist
import { CustomButton, StyledDivider } from "../../globalStyle"; // Assuming these exist
import styled, { useTheme } from "styled-components";
import { FolderOpenIcon } from "../../assets/Icons"; // Assuming this exists
import { useDispatch, useSelector } from "react-redux";
import {
  folderAccessMemberListSelector,
  folderItemSelector,
  folderListsSelector,
  isLoadingSelector,
  isRemoveUserModalOpenSelector,
  isTeamListLoadingSelector,
  membersListSelector,
  removeTeamMemberSelector,
  selectedSharedFolderSelector,
  selectedTeamFolderLIst, // This is your list of available teams
} from "../../store/metadata/selector";
import React, { useEffect, useMemo, useState } from "react";
import { MetaDataSagaActions } from "../../store/metadata/sagas";
import {
  FolderAccessMembersType,
  FolderTeamMembersType,
} from "../../store/metadata/types";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import { MetaDataReducerAction } from "../../store/metadata/slice";
import { useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  border: `1px solid #EAECEE`,
  borderRadius: "8px",
  "&:not(:last-child)": {
    marginBottom: 2,
  },
  "&::before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(() => ({
  backgroundColor: "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: "rotate(90deg)",
    },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: 5,
  },
}));

// Placeholder for custom styled components (if not directly MUI)
const StyledFormControl = FormControl; // Replace with your actual StyledFormControl if it's custom
const StyledMenuItem = MenuItem; // Replace with your actual StyledMenuItem

// Placeholder for ErrorMessage component if it's custom
const ErrorMessage = ({ children }: { children: React.ReactNode }) => (
  <Typography
    color="error"
    variant="caption"
    sx={{ mt: 0.5, display: "block" }}
  >
    {children}
  </Typography>
);

const ShareAccessToTeamAndUsersModal = () => {
  const theme = useTheme();
  const { folderId: routeFolderId } = useParams<{ folderId?: string }>(); // Ensure folderId can be undefined
  const dispatch = useDispatch();

  const [searchText, setSearchText] = useState("");
  const [isEmailError, setIsEmailError] = useState(false);
  const [myTeamListData, setMyTeamListData] = useState<
    { _id: string; name: string }[]
  >([]);
  const [selectedTeamMemberId, setSelectedTeamMemberId] = useState<
    string | null
  >(null); // For individual user add loading state

  const openAddUserModal = useSelector(isRemoveUserModalOpenSelector);
  const removeId = useSelector(removeTeamMemberSelector);
  const selectedFolder = useSelector(folderItemSelector);
  const selectedSharedFolder = useSelector(selectedSharedFolderSelector);
  const myTeamList = useSelector(selectedTeamFolderLIst); // List of all available teams
  const folderListData = useSelector(folderListsSelector);
  const folderAccessMemberList: FolderAccessMembersType = useSelector(
    folderAccessMemberListSelector
  );

  // Add safe accessors with default values
  const folderTeams = folderAccessMemberList?.teams || [];
  const folderMembers = folderAccessMemberList?.members || [];

  const [expanded, setExpanded] = useState<string | false>(false);
  const isLoadingIndividualAdd = useSelector(isLoadingSelector); // For individual user add
  const teamSelectLoading = useSelector(isTeamListLoadingSelector); // For share to team button
  const membersList = useSelector(membersListSelector);

  const currentFolderId = openAddUserModal?.isUser
    ? routeFolderId
    : selectedFolder?._id;

  const folderNameFromList = useMemo(
    () =>
      folderListData?.userFolders.find(
        (folder: any) => folder._id === routeFolderId
      )?.name,
    [routeFolderId, folderListData?.userFolders]
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue, // useful if direct manipulation is needed
  } = useForm({
    defaultValues: {
      teamIds: [] as string[],
    },
  });

  useEffect(() => {
    dispatch(MetaDataSagaActions.getInviteMembersList());
  }, [dispatch]);

  useEffect(() => {
    if (openAddUserModal?.isOpen) {
      const initialTeamIds = selectedFolder?.teamIds || [];
      const filteredTeams = myTeamList.filter(
        (team: any) => !initialTeamIds.includes(team._id)
      );
      setMyTeamListData(filteredTeams);
    }
  }, [myTeamList, selectedFolder, openAddUserModal?.isOpen, reset]);

  useEffect(() => {
    if (openAddUserModal?.isOpen && currentFolderId) {
      dispatch(
        MetaDataSagaActions.fetchFolderAccessMembersList({
          folderId: currentFolderId,
        })
      );
      dispatch(
        MetaDataReducerAction.setFolderTeamMemberList({ data: [], total: 0 })
      );
      dispatch(
        MetaDataSagaActions.fetchFolderTeamMembersList({
          folderId: currentFolderId,
          search: searchText, // This search is for individual members
        })
      );
      dispatch(MetaDataSagaActions.getSelectTeamBy()); // Fetches myTeamList
    }
  }, [openAddUserModal?.isOpen, currentFolderId, searchText, dispatch]);

  const sendInviteHandler = (value?: string) => {
    setIsEmailError(false);
    dispatch(
      MetaDataSagaActions.sendFolderInvite({
        folderId: currentFolderId,
        userEmail: value,
      })
    );
  };

  const onShareWithTeamSubmit = (formData: { teamIds: string[] }) => {
    const { teamIds } = formData;
    if (!currentFolderId || !teamIds || teamIds.length === 0) {
      console.warn("No teams selected or folder ID missing.");
      return;
    }

    dispatch(
      MetaDataSagaActions.shareFolderWithTeam({
        folderId: currentFolderId,
        teamIds: teamIds,
      })
    );
    // Consider whether these should be dispatched after all individual shares succeed.
    dispatch(MetaDataSagaActions.fetchSharedFolderList());
    if (currentFolderId && selectedFolder) {
      // Check selectedFolder for boards
      dispatch(
        MetaDataSagaActions.fetchFolderList({
          selectedFolderId: currentFolderId,
          selectedBoardId: selectedFolder?.boards[0]?._id,
        })
      );
    }
    reset({ teamIds: [] });
  };

  const RenderTeamMembersListComponent = useMemo(() => {
    const filteredMembersList = membersList?.members
      ?.filter(
        (item: FolderTeamMembersType) =>
          !folderMembers?.some(
            (member) => member?.email === item?.email
          )
      )
      ?.filter(
        (item: FolderTeamMembersType) =>
          item?.email?.toLowerCase()?.includes(searchText?.toLowerCase()) ||
          item?.fullName?.toLowerCase()?.includes(searchText?.toLowerCase())
      );

    return filteredMembersList?.length ? (
      <>
        {filteredMembersList?.map(
          (item: FolderTeamMembersType, index: number) => (
            <Grid
              item
              xs={12}
              key={item?._id}
              sx={{
                paddingBottom: "15px",
                borderBottom:
                  filteredMembersList?.length - 1 !== index
                    ? `1px solid ${theme.colors.gallery}`
                    : "none",
                marginBottom:
                  filteredMembersList?.length - 1 !== index ? "15px" : "0",
              }}
            >
              <Box
                sx={{ display: "flex", gap: "0.857rem", alignItems: "center" }}
              >
                <Avatar
                  src={item?.profileImage || "assests/images/user-logo.png"}
                  sx={{ width: "2.5rem", height: "2.5rem" }}
                />
                <Box
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography fontSize="1rem" fontWeight="500" color="black">
                      {item?.fullName}
                    </Typography>
                    <Typography
                      fontSize="0.875rem"
                      fontWeight="400"
                      color={theme.colors.gray1}
                    >
                      {item?.email}
                    </Typography>
                  </Box>
                  <CustomButton
                    onClick={() => {
                      sendInviteHandler(item?.email);
                      setSelectedTeamMemberId(item?._id);
                    }}
                    disabled={
                      isLoadingIndividualAdd &&
                      selectedTeamMemberId === item?._id
                    }
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: theme.colors.black2,
                      borderRadius: "20px",
                      border: "1px solid #EAECEE",
                      padding: "8px 16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {isLoadingIndividualAdd &&
                      selectedTeamMemberId === item?._id && (
                        <CircularProgress size={16} sx={{ mr: 0.5 }} />
                      )}
                    Add
                  </CustomButton>
                </Box>
              </Box>
            </Grid>
          )
        )}
      </>
    ) : (
      <Typography
        fontSize="1rem"
        fontWeight="600"
        textAlign="center"
        color={theme.colors.black2}
        sx={{ p: 2, m: "auto" }}
      >
        No members found!
      </Typography>
    );
  }, [searchText, isLoadingIndividualAdd, selectedTeamMemberId, theme.colors.black2, theme.colors.gallery, theme.colors.gray1, membersList?.members]);
  const handleChange =
    (panel: string) => (_event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  const removeMember = (email: string, id: string) => {
    if (currentFolderId) {
      dispatch(
        MetaDataSagaActions.removeTeamMember({
          userEmail: email,
          folderId: currentFolderId,
          userId: id,
        })
      );
    }
  };

  const removeTeam = (teamId: string) => {
    if (currentFolderId) {
      dispatch(
        MetaDataSagaActions.removeTeamFromAccess({
          teamId,
          folderId: currentFolderId,
        })
      );
    }
  };

  const handleModalClose = () => {
    dispatch(
      MetaDataReducerAction.setIsRemoveUserModalOpen({
        isOpen: false,
        isUser: false,
      })
    );
    reset({ teamIds: [] });
    setSearchText("");
    setIsEmailError(false);
    setSelectedTeamMemberId(null);
  };

  const currentModalTitle =
    routeFolderId && routeFolderId === selectedSharedFolder?._id
      ? selectedSharedFolder?.name
      : openAddUserModal?.isUser
      ? folderNameFromList
      : selectedFolder?.name;

  return (
    <Modal
      open={!!openAddUserModal?.isOpen}
      onClose={handleModalClose}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        outline: "none",
      }}
    >
      <Box
        display="flex"
        sx={{
          minWidth: { xs: "95%", md: "890px" },
          maxWidth: "890px",
          background: "white",
          borderRadius: "15px",
          minHeight: "40vh",
          maxHeight: "90vh",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* Left Pane: Shared With List */}
        <Box
          sx={{
            width: { xs: "100%", md: "45%" },
            padding: "20px",
            borderRight: {
              xs: "none",
              md: `1px solid ${theme.colors.gallery}`,
            },
            borderBottom: {
              xs: `1px solid ${theme.colors.gallery}`,
              md: "none",
            },
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ModalTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FolderOpenIcon />
            {currentModalTitle || "Share Folder"}
          </ModalTitle>
          <FontGrayNormal sx={{ mt: 1, mb: 2 }}>
            Currently shared with:
          </FontGrayNormal>

          <Box sx={{ flexGrow: 1, overflowY: "auto", pr: 1 }}>
            {folderTeams?.length ? (
              <Typography
                fontWeight="600 !important"
                fontSize="1rem !important"
                mb={1}
              >
                Teams :
              </Typography>
            ) : null}
            {/* Render Team Accordions */}
            {folderTeams?.length ? (<>
            {folderTeams?.map((team) => (
              <Accordion
                key={team._id}
                expanded={expanded === team._id}
                onChange={handleChange(team._id)}
              >
                <AccordionSummary>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: "4px",
                        justifyContent: "center",
                        alignContent: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: "20px",
                          fontSize: "14px",
                          color: theme.colors.white,
                          borderRadius: "50%",
                          border: "1px solid #EAECEE",
                          background:
                            "linear-gradient(94.04deg, #1c75bc 2.44%, #00aeef 112.73%)",
                          height: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography sx={{ fontWeight: 500, fontSize: "10px" }}>
                          {team.members?.length || 0}

                        </Typography>
                      </Box>
                      <Typography fontWeight={600}>{team.name}</Typography>
                    </Box>
                    {folderTeams?.length > 1 ? (
                      <CustomButton
                        onClick={(e) => {
                          e.stopPropagation();
                          removeTeam(team._id);
                        }}
                        disabled={removeId === team._id}
                        sx={{
                          padding: "8px 12px",
                          borderRadius: "20px",
                          border: "1px solid #EAECEE",
                          color: "#414042",
                          fontWeight: 500,
                        }}
                      >
                        {removeId === team._id && (
                          <CircularProgress size={16} sx={{ mr: 0.5 }} />
                        )}
                        Remove
                      </CustomButton>
                    ) : null}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, py: 1.5 }}>
                  {(team.members || []).map((member: any, idx: number) => (
                    <React.Fragment key={member._id}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                          mb: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: "0.857rem",
                            alignItems: "center",
                          }}
                        >
                          <Avatar
                            src={
                              member?.profileImage ||
                              "assets/images/user-logo.png"
                            }
                            sx={{ width: "2.5rem", height: "2.5rem" }}
                          />
                          <Box>
                            <Typography
                              fontSize="1rem"
                              fontWeight="500"
                              color="black"
                            >
                              {member?.fullName || "No Name"}
                            </Typography>
                            <Typography
                              fontSize="0.875rem"
                              fontWeight="400"
                              color={theme.colors.gray1}
                            >
                              {member?.userEmail}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      {team.members?.length - 1 !== idx && (
                        <Box sx={{ width: "100%", mb: "7px" }}>
                          <StyledDivider />
                        </Box>
                      )}
                    </React.Fragment>
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}
            </>): null}

            {folderMembers?.length ? (
              <Typography
                fontWeight="600 !important"
                fontSize="1rem !important"
                my={3}
              >
                Members :
              </Typography>
            ) : null}
            {folderMembers?.map((item, index) => (
              <>
                <Box
                  key={item._id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.857rem",
                      alignItems: "center",
                    }}
                  >
                    <Avatar
                      src={item?.profileImage || "assests/images/user-logo.png"}
                      sx={{ width: "2.5rem", height: "2.5rem" }}
                    />
                    <Box>
                      <Typography
                        fontSize="1rem"
                        fontWeight="500"
                        color="black"
                      >
                        {item?.fullName}
                      </Typography>
                      <Typography
                        fontSize="0.875rem"
                        fontWeight="400"
                        color={theme.colors.gray1}
                      >
                        {item?.email}
                      </Typography>
                    </Box>
                  </Box>
                  {item?.canDelete && (
                    <CustomButton
                      onClick={() => removeMember(item?.email, item?._id)}
                      disabled={removeId === item?._id}
                      sx={{
                        padding: "8px 12px",
                        borderRadius: "20px",
                        border: "1px solid #EAECEE",
                        color: "#414042",
                        fontWeight: 500,
                      }}
                    >
                      {removeId === item?._id && (
                        <CircularProgress size={16} sx={{ mr: 0.5 }} />
                      )}
                      Remove
                    </CustomButton>
                  )}
                </Box>
                {folderMembers?.length - 1 != index && (
                  <Box sx={{ width: "100%", mb: "7px" }}>
                    <StyledDivider />
                  </Box>
                )}
              </>
            ))}
            {!folderMembers?.length &&
              !folderTeams?.length && (
                <Typography
                  sx={{ textAlign: "center", color: theme.colors.gray1, mt: 2 }}
                >
                  Not shared with anyone yet.
                </Typography>
              )}
          </Box>
        </Box>

        {/* Right Pane: Add Users/Teams */}
        <Box
          sx={{
            width: { xs: "100%", md: "55%" },
            padding: "20px",
            minHeight: "50%",
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
          }}
        >
          <ModalTitle sx={{ mb: 1 }}>
            Share Access With Teams or Members
          </ModalTitle>
          <FontGrayNormal sx={{ mb: 3 }}>
            Share this folder with members or teams to give access.
          </FontGrayNormal>

          <form onSubmit={handleSubmit(onShareWithTeamSubmit)}>
            <Typography variant="subtitle1" fontWeight="500" sx={{ mb: 1 }}>
              Share with teams
            </Typography>
            <Box display="flex" alignItems="flex-start" sx={{ mb: 1 }}>
              <StyledFormControl fullWidth sx={{ marginRight: "1rem" }}>
                <Controller
                  name="teamIds"
                  control={control}
                  rules={{
                    required: "Please select at least one team to share.",
                  }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      multiple
                      displayEmpty
                      input={
                        <OutlinedInput
                          placeholder="Select teams"
                          notched={false}
                        />
                      }
                      renderValue={(selectedTeamIds: string[]) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selectedTeamIds.map((id) => {
                            const team = myTeamListData?.find(
                              (t: any) => t._id === id
                            );
                            return (
                              <Chip
                                key={id}
                                label={
                                  team
                                    ? team?.name
                                    : `ID: ${id.substring(0, 6)}...`
                                }
                                onMouseDown={(e) => e.stopPropagation()}
                                onDelete={() => {
                                  const updatedTeamIds = field.value.filter(
                                    (itemId: string) => itemId !== id
                                  );
                                  field.onChange(updatedTeamIds);
                                }}
                                size="small"
                                sx={{ borderRadius: "8px" }}
                              />
                            );
                          })}
                          {selectedTeamIds.length === 0 && (
                            <Typography
                              sx={{
                                color: theme.colors.gray1,
                                opacity: 0.7,
                                fontSize: "0.9rem",
                                pl: 0.5,
                              }}
                            >
                              Select teams
                            </Typography>
                          )}
                        </Box>
                      )}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            borderRadius: 15,
                            maxHeight: "300px",
                            overflowY: "auto",
                          },
                        },
                      }}
                      sx={{
                        borderRadius: "10px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#EAECEE",
                        },
                        "& .MuiInputBase-input": {
                          padding: "10px 14px !important",
                        },
                      }}
                    >
                      {myTeamListData?.map((team: any) => (
                        <StyledMenuItem key={team._id} value={team._id}>
                          {team.name}
                        </StyledMenuItem>
                      ))}
                      {(!myTeamListData || myTeamListData.length === 0) && (
                        <MenuItem disabled>No teams available</MenuItem>
                      )}
                    </Select>
                  )}
                />
                {errors.teamIds && (
                  <ErrorMessage>{errors.teamIds.message}</ErrorMessage>
                )}
              </StyledFormControl>
              <CustomButton
                type="submit"
                disabled={teamSelectLoading}
                style={{
                  width: "20%",
                  fontSize: "14px",
                  color: theme.colors.white,
                  borderRadius: "27px",
                  border: "1px solid #EAECEE",
                  background:
                    "linear-gradient(94.04deg, #1c75bc 2.44%, #00aeef 112.73%)",
                  padding: "14px 20px",
                  height: "45px",
                }}
              >
                {teamSelectLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : (
                  "Share"
                )}
              </CustomButton>
            </Box>
          </form>
          <StyledDivider sx={{ my: 1 }} />
          <Typography
            fontWeight="600 !important"
            fontSize="1rem !important"
            mb={1}
          >
            Members :
          </Typography>
          <Box display="flex" alignItems="center" width="100%">
            <OutlinedInput
              fullWidth
              type="search"
              value={searchText}
              error={isEmailError}
              placeholder="Search Members..."
              onChange={(e) => {
                setSearchText(e.target.value);
              }}
              sx={{
                borderRadius: "10px",
                background: "#F6F7F8 !important",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: isEmailError
                    ? theme.palette.error.main
                    : "#EAECEE",
                },
                input: {
                  padding: "10px 14px",
                  "&::placeholder": { color: theme.colors.gray1, opacity: 0.7 },
                },
              }}
            />
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            gap="10px"
            sx={{ flexGrow: 1, overflow: "hidden", mt: 1 }}
          >
            <Box
              border="1px solid #EAECEE"
              borderRadius="8px"
              display="flex"
              flexDirection="column"
              padding="15px"
              sx={{ maxHeight: "18rem", overflowY: "auto", flexGrow: 1 }}
            >
              <Grid container spacing={0}>
                {RenderTeamMembersListComponent}
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
export default ShareAccessToTeamAndUsersModal;
